import * as React from 'react';
import { Check, Plus } from 'lucide-react';
import { z } from 'zod';
import { BottomSheet, BottomSheetContent } from './BottomSheet';
import { Button } from './Button';
import { DataValue } from './DataValue';
import { Form, FormField, useZodForm } from './Form';
import { Input } from './Input';
import { Stack } from './Layout';
import { SettingsList, SettingsRow } from './Settings';
import { Text } from './Text';

/**
 * The amount is a number, not a coerced one: `z.coerce.number()` turns an
 * empty box into 0. The box reports '' for empty, which fails the type and
 * reads as "Enter the amount".
 */
export const chargeSchema = z.object({
  description: z.string().trim().min(1, 'Say what the charge is for'),
  amount: z
    .number({
      required_error: 'Enter the amount',
      invalid_type_error: 'Enter the amount',
    })
    .positive('Enter an amount above $0'),
});

export type ChargeValues = z.infer<typeof chargeSchema>;

/** An empty sheet. The amount is '' because that is what an empty box reports. */
const EMPTY = { description: '', amount: '' as unknown as number };

export interface ChargeSuggestion {
  id: string;
  description: string;
  /** Its price for one. */
  amount: number;
}

export interface ChargeComposerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Called with the charge: one typed in, or a suggestion tapped. The box
   * clears once this returns; closing the sheet is the screen's call.
   */
  onSubmit: (charge: ChargeValues) => void;
  /**
   * Charges to add with one tap, above the form: the account's own rates,
   * each at its price.
   */
  suggestions?: ChargeSuggestion[];
  suggestionsTitle?: string;
  /** The sheet's heading. */
  title?: string;
}

/**
 * Adding a charge to an invoice on a phone: a sheet with what it is for and
 * its amount, the tick top right to add it, and above them the account's
 * rates to add with a tap instead.
 *
 *   <ChargeComposer open={open} onOpenChange={setOpen}
 *     suggestions={rates} onSubmit={(charge) => { add(charge); setOpen(false); }} />
 *
 * A sheet, as NoteComposer is, so the keyboard does not cover the box. The
 * draft is the composer's own; the screen only sees the finished charge.
 */
export function ChargeComposer({
  open,
  onOpenChange,
  onSubmit,
  suggestions = [],
  suggestionsTitle = 'From the rates',
  title = 'Add a charge',
}: ChargeComposerProps) {
  const formId = React.useId();
  const form = useZodForm(chargeSchema, { defaultValues: EMPTY });

  const submit = (charge: ChargeValues) => {
    onSubmit(charge);
    form.reset(EMPTY);
  };

  return (
    <BottomSheet open={open} onOpenChange={onOpenChange}>
      <BottomSheetContent
        title={title}
        size="full"
        action={
          <Button
            type="submit"
            form={formId}
            variant="soft"
            size="icon-lg"
            pill
            aria-label="Add the charge"
          >
            <Check />
          </Button>
        }
      >
        <Stack gap={6}>
          <Form form={form} id={formId} gap={4} onSubmit={submit}>
            <FormField form={form} name="description" label="Charge">
              {(field) => (
                <Input {...field} placeholder="Fuel surcharge, gate fee" />
              )}
            </FormField>
            <FormField form={form} name="amount" label="Amount">
              {(field) => (
                <Input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="0.01"
                  name={field.name}
                  ref={field.ref}
                  onBlur={field.onBlur}
                  value={field.value ?? ''}
                  onChange={(e) =>
                    field.onChange(
                      e.target.value === '' ? '' : Number(e.target.value)
                    )
                  }
                  leadingIcon={
                    <Text variant="inherit" as="span">
                      $
                    </Text>
                  }
                  placeholder="0.00"
                />
              )}
            </FormField>
          </Form>
          {suggestions.length > 0 ? (
            <SettingsList title={suggestionsTitle}>
              {suggestions.map((suggestion) => (
                <SettingsRow
                  key={suggestion.id}
                  icon={Plus}
                  label={suggestion.description}
                  value={
                    <DataValue value={suggestion.amount} format="currency" />
                  }
                  onClick={() =>
                    onSubmit({
                      description: suggestion.description,
                      amount: suggestion.amount,
                    })
                  }
                />
              ))}
            </SettingsList>
          ) : null}
        </Stack>
      </BottomSheetContent>
    </BottomSheet>
  );
}
